import * as PIXI from 'pixi.js';

import { assertIsValidUrl, assertString } from '../../validators';
import type { AssetNetworkManager } from '../AssetNetworkManager';
import { getGlobalAssetNetworkManager } from '../AssetNetworkManager';
import { FONT_STATUS } from '../../constants';
import { FontLoader } from './FontLoader';


type FontEntry = {
    text: string;
    font: FontLoader;
};

/**
 * Retrieves the global font manager.
 * We should load the font manager as a GLOBAL object because we depend on font loading so much we
 * want to re-use as much loaded data as possible and not reload fonts.
 * To use the font loader, simply use window.fontManager
 */
export function getGlobalFontManager(): FontManager {
    // Note we check .classType instead of using instanceof because instanceof doesn't work well
    // across different contexts https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/instanceof#Description
    // and was causing unexpected behaviour in tests
    if (!window.fontManager || window.fontManager.classType !== 'FontManager') {
        // setup fontManager
        const assetNetworkManager = getGlobalAssetNetworkManager();
        window.fontManager = new FontManager({ assetNetworkManager });
    }
    return window.fontManager;
}

/**
 * Removes the global font manager.
 * Primarily for use between tests to ensure that the global font manager doesn't pollute tests
 */
export function clearGlobalFontManager() {
    if (window.fontManager) {
        window.fontManager.destroy();
        window.fontManager = null;
    }
}

/**
 * List of fallback urls for fonts. The order of fonts in this list, is the order that
 * the fallback urls will be tried.
 * When updating this list, make sure node rendering follows by updating fallback-fonts.txt
 */
const FALLBACK_FONT_URLS = [
    // Bulgarian
    // Czech
    // Danish
    // German
    // Greek
    // English
    // Spanish
    // Finnish
    // French
    // Croatian
    // Hungarian
    // Indonesian
    // Italian
    // Macedonian
    // Malay
    // Dutch
    // Norwegian Bokmål
    // Norwegian
    // Polish
    // Portugese
    // Romanian
    // Russian
    // Slovak
    // Serbian
    // Swedish
    // Turkish
    // Ukranian
    // Vietnamese
    'https://storage.googleapis.com/lumen5-site-css/NotoSans-Medium.ttf',
    // Arabic
    'https://storage.googleapis.com/lumen5-site-css/NotoKufiArabic-Medium.ttf',
    'https://storage.googleapis.com/lumen5-site-css/NotoNaskhArabic-Medium.ttf',
    // Hebrew
    'https://storage.googleapis.com/lumen5-site-css/NotoSansHebrew-Medium.ttf',
    // Hindi
    'https://storage.googleapis.com/lumen5-site-css/NotoSansDevanagari-Medium.ttf',
    // Japanese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansJP-Medium.otf',
    // Korean
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Medium.otf',
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Bold.otf', // The Bold font has additional glyphs (row 0000 to 0020) that are not in the Medium font. This was added as a quick workaround to pass some tests. Ideally we should have a better handling for whitespaces. See https://lumen5.atlassian.net/browse/LU-2284
    // Thai
    'https://storage.googleapis.com/lumen5-site-css/NotoSansThai-Medium.ttf',
    // Tagalog
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTagalog-Regular.ttf',
    // Simplified Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansSC-Medium.otf',
    // Traditional Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTC-Medium.otf',
    // Armenian
    'https://storage.googleapis.com/lumen5-site-css/NotoSansArmenian-Regular.ttf',
    // Georgian
    'https://storage.googleapis.com/lumen5-site-css/DejaVuSans.ttf',
    // Emoji
    'https://storage.googleapis.com/lumen5-site-css/NotoEmoji-Regular.ttf',
    // Gujarati - not well-supported LU-2989
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansGujarati-Regular.ttf',
    // Bengali - not well-supported see LU-2944 & LU-2945
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansBengali-Regular.ttf',
];

/**
 * A manager class to load and track Fonts which are used by Luminary. All fonts are loaded using the assetNetworkManager.
 */
export class FontManager extends PIXI.utils.EventEmitter {
    classType = 'FontManager';
    #fonts: Map<string, FontLoader> = new Map();
    #assetNetworkManager: AssetNetworkManager;

    constructor(settings) {
        super();
        this.#assetNetworkManager = settings.assetNetworkManager;
    }

    /**
     * Returns a list of all of the loaded fonts
     * @returns list of loaded fonts
     */
    get fonts(): string[] {
        return Array.from(this.#fonts.keys());
    }

    /**
     * Returns true once all fonts have been loaded
     */
    loadedAllFonts(): boolean {
        return this.fonts.every(sourceUrl => !!this.getFontBySourceIfLoaded(sourceUrl));
    }

    /**
     * Retrieve a fallback font url by index.
     */
    getFallbackUrlByIndex(index: number): string {
        return FALLBACK_FONT_URLS[index];
    }

    /**
     * Returns true if we've loaded all fallback fonts
     */
    loadedAllFallbackFonts(): boolean {
        return FALLBACK_FONT_URLS.every((sourcUrl) => !!this.getFontBySourceIfLoaded(sourcUrl));
    }

    /**
     * Returns true if the given sourceUrl is a fallback font
     */
    isFallbackUrl(sourceUrl: string): boolean {
        return FALLBACK_FONT_URLS.indexOf(sourceUrl) !== -1;
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font has loaded.
     */
    getFontBySourceIfLoaded(sourceUrl: string): FontLoader | null {
        if (!this.#fonts.has(sourceUrl)) {
            return null;
        }

        if (this.#fonts.get(sourceUrl).isLoaded) {
            return this.#fonts.get(sourceUrl);
        }
    }

    /**
     * Returns the fullName of the desired font. Note:
     * - the font has to be finished loading, otherwise an empty string is returned
     * - sometimes the metadata in the font file is missing a name
     */
    getFullNameOfLoadedFont(sourceUrl: string): string {
        const font = this.getFontBySourceIfLoaded(sourceUrl);
        if (!font) {
            return '';
        }
        return font.font.fullName || '';
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font exists
     */
    getFontBySource(sourceUrl: string): FontLoader {
        if (!this.#fonts.has(sourceUrl)) {
            return null;
        }
        return this.#fonts.get(sourceUrl);
    }

    /**
     * Returns a promise for the given sourceUrl which resolves once it is loaded
     */
    async getFontBySourceOnceLoaded(sourceUrl: string): Promise<FontLoader> {
        if (!this.getFontBySource(sourceUrl)) {
            this.addFont(sourceUrl);
        }
        const font = this.getFontBySource(sourceUrl);
        if (!font.isLoaded) {
            await new Promise<void>((resolve) => font.once('loaded', () => resolve()));
            return font;
        }
        return font;
    }

    /**
     * Add a new FontLoader from a ttf/otf url if it hasn't been added yet.
     * @param sourceUrl - ttf/otf url
     */
    addFont(sourceUrl: string): FontLoader {
        assertString(sourceUrl);
        if (this.#fonts.has(sourceUrl)) {
            return this.#fonts.get(sourceUrl);
        }
        const font = new FontLoader({
            parent: this,
            sourceUrl,
        });
        this.#fonts.set(sourceUrl, font);

        this.#assetNetworkManager.waitForNetworkSlot(font, requestFinished => {
            font.load().catch((e) => {
                console.error(`Failed to load font ${sourceUrl}`, e);
                font.status = FONT_STATUS.FAILED;
            }).finally(() => {
                requestFinished();
                if (font.status !== FONT_STATUS.FAILED) {
                    this.emit('loaded', sourceUrl);
                    font.emit('loaded');
                }
            });
        });

        return font;
    }

    /**
     * Removes the internal fonts map.
     */
    destroy() {
        this.removeAllFonts();
        this.removeAllListeners();
    }

    removeAllFonts() {
        this.#fonts.forEach((font) => {
            font.removeAllListeners();
        });
        this.#fonts = new Map();
    }

    /**
     * Returns True if the fontManager contains the necessary fonts to render the given string. We start by inspecting
     * the font from the given sourceUrl and work our way through the fallback fonts.
     */
    hasAllNecessaryFonts(text: string, fontUrl: string): boolean {
        assertString(text);
        const { supported } = this.canRenderText(text, fontUrl);
        return supported;
    }

    /**
     * Using the provided fontUrl and any fallback fonts, this determines if the entire text can
     * be rendered. If it can render everything, it will return a supported value of true,
     * otherwise missing will contain a string of characters which cannot currently be rendered.
     * If the primary font is not loaded it will report the entire text as not supported
     * @param text - the text to check support for
     * @param fontUrl - the primary font to use
     * @param excludeFallbackUrls - any fallback fonts which should be excluded from the check
     */
    canRenderText(text: string, fontUrl: string, excludeFallbackUrls: string[] = []) {
        const font = this.getFontBySourceIfLoaded(fontUrl);
        let missing = text;
        if (!font) {
            return { supported: false, missing };
        }

        const result = font.canRenderText(text);
        if (result.supported) {
            return result;
        }
        missing = result.missing;

        // fontUrl is not able to fully render the text so check the fallbacks
        for (let i = 0; i < FALLBACK_FONT_URLS.length; i++) {
            const currentUrl = FALLBACK_FONT_URLS[i];
            if (excludeFallbackUrls.includes(currentUrl)) {
                continue;
            }

            const font = this.getFontBySourceIfLoaded(currentUrl);
            if (!font) {
                continue;
            }

            const result = font.canRenderText(missing);
            if (result.supported) {
                return result;
            }
            missing = result.missing;
        }

        return { supported: false, missing };
    }

    /**
     * Loads the given sourceUrl and all fallback fonts to enable the rendering of the given string.
     */
    async loadAllNecessaryFonts(string: string, sourceUrl: string): Promise<boolean> {
        let font = await this.getFontBySourceOnceLoaded(sourceUrl);
        let { supported, missing } = font.canRenderText(string);
        if (supported) {
            return supported;
        }
        for (let i = 0; i < FALLBACK_FONT_URLS.length; i++) {
            font = await this.getFontBySourceOnceLoaded(FALLBACK_FONT_URLS[i]);
            ({ supported, missing } = font.canRenderText(missing));
            if (supported) {
                return supported;
            }
        }
        // if we reach this then we've loaded all our fallback fonts but still aren't able to render all the characters
        return supported;
    }

    /**
     * Returns a list containing string, font pairs. Each pair contains a section of the given string which can be
     * rendered with the paired font. For instance: Thдуйis will return 3 pairs: [{text: 'Th', font: font1}, {text: дуй,
     * font: font2}, {text: is, font: font1}].
     */
    getFontListForString(string: string, sourceUrl: string): FontEntry[] {
        assertString(string);
        assertIsValidUrl(sourceUrl);

        const result: FontEntry[] = [];
        let currFont: FontLoader = null;
        let startIndex = 0;
        for (let currIndex = 0; currIndex < string.length; currIndex++) {
            const font = this.getValidFontForCharacter(string[currIndex], sourceUrl);
            if (!font) {
                continue;
            }
            if (currFont === null) {
                currFont = font;
            }
            if (currFont.sourceUrl === font.sourceUrl) {
                // no split
                continue;
            }
            const text = string.slice(startIndex, currIndex);
            result.push({ text: text, font: currFont });
            startIndex = currIndex;
            currFont = font;
        }
        if (currFont) {
            const text = string.slice(startIndex, string.length);
            result.push({ text: text, font: currFont });
        }
        return result;
    }

    /**
     * Returns the font required to render the given character starting with the expected sourceUrl. If the font for the
     * given sourceUrl doesn't contain the glyph for the given character, then we look for a match through our fallback
     * fonts.
     */
    getValidFontForCharacter(character: string, sourceUrl: string): FontLoader {
        assertString(character);
        assertIsValidUrl(sourceUrl);
        const font = this.getFontBySourceIfLoaded(sourceUrl);
        if (!font) {
            return;
        }
        let { supported, missing } = font.canRenderText(character);
        if (supported) {
            return font;
        }
        for (let i = 0; i < FALLBACK_FONT_URLS.length; i++) {
            const font = this.getFontBySourceIfLoaded(FALLBACK_FONT_URLS[i]);
            if (!font) {
                return;
            }
            ({ supported, missing } = font.canRenderText(missing));
            if (supported) {
                return font;
            }
        }
        console.error(`No fallback font found for character '${character}', using font ${sourceUrl}`);
    }

    getLoadedFallbackFontFamilyNames(): string[] {
        const familyNames = [];
        FALLBACK_FONT_URLS.forEach((url) => {
            if (this.getFontBySourceIfLoaded(url)) {
                familyNames.push(this.getFontBySourceIfLoaded(url).familyName);
            }
        });
        return familyNames;
    }
}
